import { useEffect, useState, useCallback } from 'react'
import { useRoomContext, useLocalParticipant } from '@livekit/components-react'
import { RoomEvent } from 'livekit-client'

const REACTIONS = [
  { emoji: '\uD83D\uDC4D', label: 'Pouce' },
  { emoji: '\uD83D\uDC4F', label: 'Applaudissements' },
  { emoji: '\uD83D\uDE02', label: 'Rire' },
  { emoji: '\u2764\uFE0F', label: 'Coeur' },
  { emoji: '\uD83D\uDE2E', label: 'Surpris' },
  { emoji: '\uD83D\uDE22', label: 'Triste' },
]

interface ActiveReaction {
  id: string
  identity: string
  emoji: string
  timestamp: number
}

export function useReactions() {
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const [activeReactions, setActiveReactions] = useState<ActiveReaction[]>([])
  const encoder = new TextEncoder()
  const decoder = new TextDecoder()

  const sendReaction = useCallback((emoji: string) => {
    const msg = JSON.stringify({ type: 'reaction', emoji })
    localParticipant.publishData(encoder.encode(msg), { reliable: false })
    // Also show locally
    const id = `${localParticipant.identity}-${Date.now()}`
    setActiveReactions(prev => [...prev, { id, identity: localParticipant.identity, emoji, timestamp: Date.now() }])
  }, [localParticipant])

  // Listen for remote reactions
  useEffect(() => {
    if (!room) return
    const handler = (payload: Uint8Array, participant: any) => {
      try {
        const data = JSON.parse(decoder.decode(payload))
        if (data.type === 'reaction' && data.emoji) {
          const identity = participant?.identity || 'unknown'
          const id = `${identity}-${Date.now()}`
          setActiveReactions(prev => [...prev, { id, identity, emoji: data.emoji, timestamp: Date.now() }])
        }
      } catch {}
    }
    room.on(RoomEvent.DataReceived, handler)
    return () => { room.off(RoomEvent.DataReceived, handler) }
  }, [room])

  // Auto-clear reactions after 4s
  useEffect(() => {
    if (activeReactions.length === 0) return
    const timer = setInterval(() => {
      const now = Date.now()
      setActiveReactions(prev => prev.filter(r => now - r.timestamp < 4000))
    }, 500)
    return () => clearInterval(timer)
  }, [activeReactions.length])

  return { activeReactions, sendReaction, REACTIONS }
}

export function ReactionPicker({ onReact, onClose }: { onReact: (emoji: string) => void; onClose: () => void }) {
  return (
    <div className="absolute bottom-full mb-2 left-0 bg-zinc-800 border border-zinc-700 rounded-lg p-2 shadow-xl z-50 flex gap-1">
      {REACTIONS.map(r => (
        <button
          key={r.emoji}
          onClick={() => { onReact(r.emoji); onClose() }}
          className="w-8 h-8 flex items-center justify-center text-lg hover:bg-zinc-700 rounded transition-colors"
          title={r.label}
        >
          {r.emoji}
        </button>
      ))}
    </div>
  )
}

export function ReactionBubble({ emoji }: { emoji: string }) {
  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-reaction-float">
      <span className="text-3xl">{emoji}</span>
    </div>
  )
}
